import { createApiPath } from '@/shared/api/endpoints';
import { showNetworkErrorToast } from '@/shared/utils/toast';
import axios, {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * 개발 서버에서는 Vite 프록시(/api → dev API)를 쓰므로 baseURL을 비운다.
 * 빌드 결과물은 env의 VITE_API_URL로 직접 호출한다.
 */
const API_BASE_URL = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

export const TOKEN_KEY = 'rudalink-web:access-token';
export const REFRESH_TOKEN_KEY = 'rudalink-web:refresh-token';

/** 서버 공통 에러 형식 — 성공 응답은 envelope 없이 DTO 그대로 온다 */
export interface ApiErrorBody {
  code?: string;
  message?: string;
  errors?: Array<{ field: string; message: string }>;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let isEndingSession = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((item) =>
    error ? item.reject(error) : token && item.resolve(token),
  );
  failedQueue = [];
};

/**
 * 재발급 루프에서 제외할 요청.
 * 이메일 · SNS 로그인 실패도 401로 오므로 함께 뺀다.
 */
const isAuthEndpoint = (url?: string) =>
  !!url &&
  (url.includes(createApiPath.auth.login()) ||
    url.includes(createApiPath.auth.refresh()) ||
    url.includes('/web/auth/social/'));

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/** refresh 실패 = 무조건 재로그인. 로그인 화면에서 X7 토스트를 띄운다 */
const endSession = () => {
  if (isEndingSession) return;
  isEndingSession = true;
  clearTokens();
  localStorage.removeItem('rudalink-web-auth');
  const here = window.location.pathname + window.location.search;
  const redirect = here.startsWith('/login')
    ? ''
    : `&redirect=${encodeURIComponent(here)}`;
  window.location.href = `/login?reason=expired${redirect}`;
};

/** interceptor를 타지 않는 별도 호출로 무한 루프를 막는다. access · refresh 둘 다 새로 온다 */
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error('refresh token 없음');

  const { data } = await axios.post<{ token: string; refreshToken: string }>(
    `${API_BASE_URL}${createApiPath.auth.refresh()}`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' }, timeout: 15000 },
  );
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  return data.token;
};

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequestConfig | undefined;

    // X8 — 응답이 아예 없으면(연결 끊김 · 타임아웃) 모든 화면에서 같은 토스트를 띄운다.
    // 로그인 화면은 폼 아래 문구로 따로 보여주므로 인증 요청은 뺀다. 요청 취소는 오류가 아니다.
    if (
      !error.response &&
      !axios.isCancel(error) &&
      !isAuthEndpoint(original?.url)
    ) {
      showNetworkErrorToast();
    }

    // 만료된 access token의 401은 body에 code가 없을 수 있어 상태 코드로만 판단한다
    if (
      error.response?.status !== 401 ||
      !original ||
      original._retry ||
      isAuthEndpoint(original.url)
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return apiClient(original);
      });
    }

    original._retry = true;
    isRefreshing = true;
    try {
      const token = await refreshAccessToken();
      processQueue(null, token);
      original.headers.Authorization = `Bearer ${token}`;
      return apiClient(original);
    } catch (refreshError) {
      processQueue(refreshError);
      endSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/** 응답이 아예 없는 경우(X8 네트워크 오류) */
export const isNetworkError = (error: unknown) =>
  axios.isAxiosError(error) && !error.response;

export const getApiErrorCode = (error: unknown): string | undefined =>
  axios.isAxiosError<ApiErrorBody>(error)
    ? error.response?.data?.code
    : undefined;
